import React, { useState, useEffect } from 'react';
import { legalService } from '../services/legalService';
import LegalAcceptanceModal from './LegalAcceptanceModal';
import { NEEDS_LEGAL_ACCEPTANCE_EVENT } from '../utils/apiFetch';
import { useAuth } from '../contexts/AuthContext';

interface LegalAcceptanceGateProps {
  children: React.ReactNode;
}

const LegalAcceptanceGate: React.FC<LegalAcceptanceGateProps> = ({ children }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [needsAcceptance, setNeedsAcceptance] = useState(false);
  const [tosVersion, setTosVersion] = useState('');
  const [privacyVersion, setPrivacyVersion] = useState('');
  const [error, setError] = useState<string | null>(null);

  const checkStatus = async () => {
    if (user?.isKiosk) {
      setNeedsAcceptance(false);
      setError(null);
      setLoading(false);
      return;
    }
    try {
      const status = await legalService.getStatus();
      setNeedsAcceptance(status.needsAcceptance);
      setTosVersion(status.tosVersion);
      setPrivacyVersion(status.privacyVersion);
      setError(null);
    } catch (e: any) {
      setError(e?.message || 'Failed to verify legal status');
      setNeedsAcceptance(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.isKiosk) {
      setNeedsAcceptance(false);
      setError(null);
      setLoading(false);
      return;
    }
    checkStatus();
  }, [user?.isKiosk]);

  useEffect(() => {
    const handleNeedsLegal = () => checkStatus();
    window.addEventListener(NEEDS_LEGAL_ACCEPTANCE_EVENT, handleNeedsLegal);
    return () => window.removeEventListener(NEEDS_LEGAL_ACCEPTANCE_EVENT, handleNeedsLegal);
  }, []);

  const handleAccepted = () => {
    setNeedsAcceptance(false);
    checkStatus();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <span className="loading loading-spinner loading-lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center min-h-screen p-4">
        <div className="alert alert-error max-w-md">
          <span>{error}</span>
        </div>
      </div>
    );
  }

  if (needsAcceptance) {
    return (
      <>
        <div className="min-h-screen bg-base-200" aria-hidden="true" />
        <LegalAcceptanceModal
          tosVersion={tosVersion}
          privacyVersion={privacyVersion}
          onAccept={handleAccepted}
        />
      </>
    );
  }

  return <>{children}</>;
};

export default LegalAcceptanceGate;
